import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import jQueryModule from 'jquery';

// Import summernote-lite v0.9.0 via centralized index
// This ensures jquery-global is loaded (sets window.jQuery) and summernote registers on it.
import './summernote';
import { syncToolbarFontLabels } from './summernote/toolbarFontSync';

// Resolve the jQuery instance that actually has summernote registered.
//
// In multi-package builds (e.g. the chat package importing web's InfoWindow),
// several jQuery copies can coexist: chat's own instance, the deduped root copy,
// and the one summernote-lite pre-bundles against. `import './summernote'` registers
// $.fn.summernote on ONE of them, but `window.jQuery` (or the module import) may point
// at a DIFFERENT one — leaving `editorRef.current.summernote is not a function` at init.
//
// So, instead of capturing a single instance at module-load time, pick at call time the
// candidate that actually exposes $.fn.summernote.
const resolveJQuery = (): any =>
{
  const candidates = [(window as any).jQuery, (window as any).$, jQueryModule];
  for (const candidate of candidates)
  {
    if (candidate && candidate.fn && typeof candidate.fn.summernote === 'function')
    {
      return candidate;
    }
  }
  // Fallback: nothing has summernote registered yet; return the best guess so the
  // original (clear) error surfaces instead of a "$ is undefined" one.
  return (window as any).jQuery || jQueryModule;
};

const randomUid = () => Math.floor(Math.random() * 100000);

// Summernote's toolbar dropdowns (.note-dropdown-menu) use `position: absolute`
// relative to their `.note-btn-group` button — they stay in normal layout flow.
// Inside a scrollable modal body, a tall dropdown (e.g. the expanded font list)
// inflates that ancestor's scrollHeight and triggers an unwanted scrollbar,
// instead of floating on top of the content like a real popover.
//
// Fix: watch for the `open` class Summernote toggles on `.note-btn-group` and,
// the instant it appears, switch that group's dropdown to `position: fixed`
// with coordinates computed from the button's own bounding rect — removing it
// from the scrollable flow entirely without needing to reparent the jQuery-owned
// DOM node into a portal.
const enableFixedDropdowns = (root: HTMLElement | null | undefined): (() => void) =>
{
  if (!root)
  {
    return () => {};
  }

  const positionMenu = (group: HTMLElement) =>
  {
    const menu = group.querySelector<HTMLElement>('.note-dropdown-menu');
    if (!menu) return;

    const rect = group.getBoundingClientRect();
    const menuWidth = menu.offsetWidth;

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - 8)
    {
      left = Math.max(8, window.innerWidth - menuWidth - 8);
    }

    menu.style.position = 'fixed';
    menu.style.top = `${rect.bottom}px`;
    menu.style.left = `${left}px`;
    menu.style.marginLeft = '0';
  };

  const observer = new MutationObserver(mutations =>
  {
    for (const mutation of mutations)
    {
      const target = mutation.target as HTMLElement;
      if (target.classList?.contains('note-btn-group') && target.classList.contains('open'))
      {
        positionMenu(target);
      }
    }
  });

  observer.observe(root, { attributes: true, attributeFilter: ['class'], subtree: true });

  return () => observer.disconnect();
};

interface ReactSummernoteCustomProps {
  value?: string;
  defaultValue?: string;
  codeview?: boolean;
  className?: string;
  options?: any;
  disabled?: boolean;
  onInit?: (methods: SummernoteAPI) => void;
  onEnter?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyUp?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  onPaste?: (e: any) => void;
  onChange?: (content: string) => void;
  onImageUpload?: (images: File[], insertImage: (url: string, filename?: string) => void) => void;
}

export interface SummernoteAPI {
  summernote: (command: string, ...args: any[]) => any;
  focus: () => void;
  isEmpty: () => boolean;
  reset: () => void;
  replace: (content: string) => void;
  disable: () => void;
  enable: () => void;
  insertImage: (url: string, filenameOrCallback?: string | Function) => void;
  insertNode: (node: Node) => void;
  insertText: (text: string) => void;
  editor: any;
  noteEditable: any;
}

const ReactSummernoteCustom = forwardRef<SummernoteAPI, ReactSummernoteCustomProps>(
  (props, ref) =>
  {
    const {
      value,
      defaultValue,
      codeview = false,
      className,
      options = {},
      disabled,
      onInit,
      onEnter,
      onFocus,
      onBlur,
      onKeyUp,
      onKeyDown,
      onPaste,
      onChange,
      onImageUpload,
    } = props;

    const uidRef = useRef(`react-summernote-${randomUid()}`);
    const editorRef = useRef<any>(null);
    const noteEditableRef = useRef<any>(null);
    const notePlaceholderRef = useRef<any>(null);
    const previousValueRef = useRef<string | undefined>(value);
    // Summernote owns this node once initialized, so the seed HTML is captured
    // once. Re-applying it on later renders would wipe the editor's DOM - and
    // force a fresh decode of every inline image - on each keystroke.
    const initialHtmlRef = useRef<string>(value || defaultValue || '');
    const previousDisabledRef = useRef<boolean | undefined>(disabled);
    const previousCodeviewRef = useRef<boolean>(codeview);
    const toolbarSavedRangeRef = useRef<Range | null>(null);
    const dropdownCleanupRef = useRef<() => void>(() => {});

    // Latest-prop refs — updated every render so Summernote's one-time callbacks
    // always call the current version, not the stale closure from mount.
    const onEnterRef = useRef(onEnter);
    const onFocusRef = useRef(onFocus);
    const onBlurRef = useRef(onBlur);
    const onKeyUpRef = useRef(onKeyUp);
    const onKeyDownRef = useRef(onKeyDown);
    const onPasteRef = useRef(onPaste);
    const onChangeRef = useRef(onChange);
    const onImageUploadRef = useRef(onImageUpload);
    const onInitRef = useRef(onInit);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() =>
    {
      onEnterRef.current = onEnter;
    }, [onEnter]);
    useEffect(() =>
    {
      onFocusRef.current = onFocus;
    }, [onFocus]);
    useEffect(() =>
    {
      onBlurRef.current = onBlur;
    }, [onBlur]);
    useEffect(() =>
    {
      onKeyUpRef.current = onKeyUp;
    }, [onKeyUp]);
    useEffect(() =>
    {
      onKeyDownRef.current = onKeyDown;
    }, [onKeyDown]);
    useEffect(() =>
    {
      onPasteRef.current = onPaste;
    }, [onPaste]);
    useEffect(() =>
    {
      onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() =>
    {
      onImageUploadRef.current = onImageUpload;
    }, [onImageUpload]);
    useEffect(() =>
    {
      onInitRef.current = onInit;
    }, [onInit]);

    // Callbacks object — created once, delegates to latest-prop refs so
    // drag-and-drop, paste, keydown, and image upload always use fresh handlers.
    const callbacks = useRef({
      onInit: () =>
      {
        const $container = editorRef.current.parent();
        noteEditableRef.current = $container.find('.note-editable');
        notePlaceholderRef.current = $container.find('.note-placeholder');

        syncToolbarFontLabels($container, noteEditableRef.current?.[0], optionsRef.current?.fontNames);

        dropdownCleanupRef.current = enableFixedDropdowns($container[0]);

        if (typeof disabled === 'boolean')
        {
          toggleState(disabled);
        }

        if (typeof onInitRef.current === 'function')
        {
          onInitRef.current({
            summernote: editorRef.current.summernote.bind(editorRef.current),
            focus,
            isEmpty,
            reset,
            replace,
            disable,
            enable,
            insertImage,
            insertNode,
            insertText,
            editor: editorRef.current,
            noteEditable: noteEditableRef.current,
          });
        }

        const $ = resolveJQuery();

        // Save range on blur so it can be restored when the color-picker
        // dropdown is clicked (editor loses focus at that point).
        noteEditableRef.current.on('blur', function ()
        {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0)
          {
            const r = sel.getRangeAt(0);
            const editableEl = noteEditableRef.current?.[0];
            if (editableEl && editableEl.contains(r.commonAncestorContainer))
            {
              toolbarSavedRangeRef.current = r.cloneRange();
            }
          }
        });

        // Backup: save on toolbar mousedown (fires before blur in some cases)
        const $toolbar = $container.find('.note-toolbar');
        $toolbar.on('mousedown', function ()
        {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0)
          {
            const r = sel.getRangeAt(0);
            const editableEl = noteEditableRef.current?.[0];
            if (editableEl && editableEl.contains(r.commonAncestorContainer))
            {
              toolbarSavedRangeRef.current = r.cloneRange();
            }
          }
        });

        // Restore range on non-actionable clicks in the color-picker dropdown.
        // Must attach directly to .note-dropdown-menu (bypasses Summernote's
        // stopPropagation). Only restore for non-buttons — .note-holder divs have
        // data-event but are containers; clicking them causes foreColor(undefined).
        // Exclude <a data-value> elements (fontname/fontsize items) — they invoke
        // a command and must NOT restore the selection, or they overwrite the FEFF
        // cursor anchor that fontStyling just inserted.
        $container.find('.note-dropdown-menu').on('click', function (e: any)
        {
          const $target = $(e.target);
          const isActionable = !!$target.closest('button[data-event], input[data-event], a[data-value]').length;
          if (!isActionable && toolbarSavedRangeRef.current)
          {
            const editableEl = noteEditableRef.current?.[0];
            if (editableEl)
            {
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(toolbarSavedRangeRef.current);
              editableEl.focus();
            }
          }
        });

        // Fontname/fontsize items apply their command on 'click', but by then
        // Summernote's own beforeCommand() has already run: it calls focus()
        // on an editable that isn't focused (focus is on the dropdown link),
        // and re-focusing a contenteditable with no live selection inside it
        // resets the caret to the very start of the content instead of
        // wherever the user actually was. Restore the saved range and focus
        // the editable on 'mousedown' — before that command fires — so
        // beforeCommand() sees it already focused with the right range and
        // skips the reset.
        $container.find('.note-dropdown-menu').on('mousedown', 'a[data-value]', function ()
        {
          const editableEl = noteEditableRef.current?.[0];
          if (editableEl && toolbarSavedRangeRef.current)
          {
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(toolbarSavedRangeRef.current);
            editableEl.focus();
          }
        });
      },
      onEnter: () => onEnterRef.current?.(),
      onFocus: () => onFocusRef.current?.(),
      onBlur: () => onBlurRef.current?.(),
      onKeyup: (e: any) => onKeyUpRef.current?.(e),
      onKeydown: (e: any) =>
      {
        // Re-select the FEFF cursor anchor before the browser processes the
        // keystroke. This fires before handleKeyMap and setLastRange(), so
        // lastRange is saved at the FEFF position and the typed character
        // replaces it, inheriting the font/size that was applied without a
        // text selection.
        if (noteEditableRef.current?.[0])
        {
          const editableEl = noteEditableRef.current[0];
          const walker = document.createTreeWalker(editableEl, NodeFilter.SHOW_TEXT);
          let feffNode: Text | null = null;
          let tNode: Node | null;
          while ((tNode = walker.nextNode()))
          {
            if ((tNode as Text).textContent === '\uFEFF')
            {
              feffNode = tNode as Text;
              break;
            }
          }
          if (feffNode)
          {
            const sel = window.getSelection();
            if (sel?.anchorNode !== feffNode || sel?.focusNode !== feffNode)
            {
              const feffRange = document.createRange();
              feffRange.setStart(feffNode, 0);
              feffRange.setEnd(feffNode, feffNode.length);
              sel?.removeAllRanges();
              sel?.addRange(feffRange);
            }
          }
        }

        // Summernote's own keydown handler only calls setLastRange() (refreshing
        // its cached selection) AFTER handleKeyMap runs the TAB/shortcut command
        // for the CURRENT keystroke - so that command still reads the range left
        // over from BEFORE the previous keystroke's native character insertion.
        // Typing a character and immediately pressing Tab (fast typing, key
        // rollover) lands the tab marker one position behind the caret, next to
        // the just-typed letter, instead of at the actual cursor. Force a resync
        // to the live selection here, before Summernote reads it.
        if ((e.keyCode === 9 || e.key === 'Tab') && editorRef.current)
        {
          editorRef.current.summernote('createRange');
        }

        onKeyDownRef.current?.(e);
      },
      onPaste: (e: any) => onPasteRef.current?.(e),

      onChange: (content: string) =>
      {
        const prevContent = previousValueRef.current;
        previousValueRef.current = content;

        // When the editor becomes empty, reset HTML and clear any pending
        // execCommand color state (foreColor/backColor set via execCommand
        // persist independently of DOM content — removeFormat resets them).
        //
        // When Summernote applies a font/size to a collapsed range it inserts a
        // zero-width no-break space (U+FEFF) as a cursor anchor inside the new
        // span. JavaScript's trim() treats U+FEFF as whitespace so innerText
        // would appear empty — skip the reset so the span survives until the user types.
        const hasBogusChar = content.includes('\uFEFF');

        // Summernote's TAB command inserts literal NBSP characters (\u00A0) as
        // the tab's visible spacing (dom.NBSP_CHAR), not a real \t. `content` is
        // serialized HTML (from $editable.html()), and the HTML serializer
        // escapes NBSP text as the "&nbsp;" entity rather than the raw
        // char, so check for the entity here. JS's String.trim() treats the
        // actual \u00A0 character (once parsed back into the DOM) as
        // whitespace, so an editor containing only tab spacing looks "empty"
        // via innerText.trim() below and would get wiped back to <p><br></p>
        // right after Summernote inserts it - most noticeable when Tab is
        // pressed in an otherwise-empty editor, since any surrounding real
        // text would keep innerText non-blank instead.
        const hasTabNbsp = content.includes('&nbsp;');

        // Ensure the FEFF cursor anchor is selected so the next keystroke
        // replaces it. Summernote selects it after a collapsed-range font/size
        // change, but when the range is already non-collapsed (e.g. the user
        // changes font a second time without typing) it applies the style to
        // the existing selection and may leave the caret in the wrong position.
        if (hasBogusChar && noteEditableRef.current?.[0])
        {
          const editableEl = noteEditableRef.current[0];

          // Once real text lands next to the FEFF anchor its job is done —
          // but the browser appends the typed character beside it instead of
          // replacing the selected anchor, so the char survives as a
          // permanent invisible prefix (e.g. "[FEFF]Arial") unless removed
          // here. Left alone, it clutters the saved HTML forever (it never
          // matches the exact-FEFF check below, since that only targets an
          // untouched, not-yet-typed-into anchor).
          const activeRange = (() =>
          {
            const sel = window.getSelection();
            return sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
          })();
          const cleanupWalker = document.createTreeWalker(editableEl, NodeFilter.SHOW_TEXT);
          let staleNode: Node | null;
          while ((staleNode = cleanupWalker.nextNode()))
          {
            const textNode = staleNode as Text;
            const text = textNode.textContent ?? '';
            const feffIndex = text.indexOf(String.fromCharCode(0xFEFF));
            if (feffIndex === -1 || text.length <= 1) continue;

            const caretOffset =
              activeRange && activeRange.startContainer === textNode ? activeRange.startOffset : null;
            const caretInThisNode = caretOffset !== null;

            textNode.textContent = text.slice(0, feffIndex) + text.slice(feffIndex + 1);

            if (caretInThisNode && caretOffset !== null)
            {
              const newOffset = caretOffset > feffIndex ? caretOffset - 1 : caretOffset;
              const clampedOffset = Math.max(0, Math.min(newOffset, textNode.textContent.length));
              const restoredRange = document.createRange();
              restoredRange.setStart(textNode, clampedOffset);
              restoredRange.collapse(true);
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(restoredRange);
            }
          }

          const walker = document.createTreeWalker(editableEl, NodeFilter.SHOW_TEXT);
          let feffNode: Text | null = null;
          let tNode: Node | null;
          while ((tNode = walker.nextNode()))
          {
            if ((tNode as Text).textContent === '\uFEFF')
            {
              feffNode = tNode as Text;
              break;
            }
          }
          if (feffNode)
          {
            const sel = window.getSelection();
            const alreadySelected =
              sel?.anchorNode === feffNode && sel?.focusNode === feffNode;
            if (!alreadySelected)
            {
              const feffRange = document.createRange();
              feffRange.setStart(feffNode, 0);
              feffRange.setEnd(feffNode, feffNode.length);
              sel?.removeAllRanges();
              sel?.addRange(feffRange);
            }

            // Preserve the counterpart font property so changing font-size
            // doesn't drop font-family and vice versa.
            //
            // When Summernote inserts the FEFF span at a collapsed cursor that
            // sits at the EDGE of an existing styled span, the new span becomes
            // a SIBLING (not a child) of that span. Walking only ancestors never
            // reaches sibling spans, so we must also search the adjacent siblings.
            const feffSpan = feffNode.parentElement as HTMLElement | null;
            if (feffSpan)
            {
              const hasFontFamily = !!feffSpan.style.fontFamily;
              const hasFontSize = !!feffSpan.style.fontSize;

              // Returns the inline value of a font property from an element or,
              // recursively, from its last-child descendant chain (to handle
              // nested inline spans).
              const getFontFromEl = (el: HTMLElement, isFontFamily: boolean): string =>
              {
                const val = isFontFamily ? el.style.fontFamily : el.style.fontSize;
                if (val) return val;
                let child = el.lastElementChild;
                while (child instanceof HTMLElement)
                {
                  const childVal = isFontFamily ? child.style.fontFamily : child.style.fontSize;
                  if (childVal) return childVal;
                  child = child.lastElementChild;
                }
                return '';
              };

              // Search ancestors, then adjacent siblings (prev then next).
              const findFontProp = (isFontFamily: boolean): string =>
              {
                let el: Element | null = feffSpan.parentElement;
                while (el && el !== editableEl)
                {
                  if (el instanceof HTMLElement)
                  {
                    const v = isFontFamily ? el.style.fontFamily : el.style.fontSize;
                    if (v) return v;
                  }
                  el = el.parentElement;
                }
                const prev = feffSpan.previousElementSibling;
                if (prev instanceof HTMLElement)
                {
                  const v = getFontFromEl(prev, isFontFamily);
                  if (v) return v;
                }
                const next = feffSpan.nextElementSibling;
                if (next instanceof HTMLElement)
                {
                  const v = getFontFromEl(next, isFontFamily);
                  if (v) return v;
                }
                return '';
              };

              if (hasFontSize && !hasFontFamily)
              {
                const family = findFontProp(true);
                if (family) feffSpan.style.fontFamily = family;
              }
              else if (hasFontFamily && !hasFontSize)
              {
                const size = findFontProp(false);
                if (size) feffSpan.style.fontSize = size;
              }
            }
          }
        }

        // innerText is blank for non-text content too (image, hr, table, list) — don't
        // treat those as empty, or a paste with no visible text gets wiped.
        const hasNonTextContent = !!noteEditableRef.current?.[0]?.querySelector(
          'img, input, hr, table, iframe, video, audio, embed, object, canvas, svg, ul, ol',
        );

        // Paragraphs with styles (alignment, indentation) have empty innerText
        // but carry meaningful formatting — don't reset them to <p><br></p>.
        const hasStyledBlocks = !!noteEditableRef.current?.[0]?.querySelector('[style]');

        // "Empty" must mean the state the browser leaves behind once everything
        // is deleted (<p><br></p>, <p></p>, a bare <br>, or nothing) — NOT merely
        // "no visible text". Enter on an empty editor produces a second blank
        // block and Space produces a lone space/NBSP; both have a blank
        // innerText (JS trim() also strips U+00A0), so an innerText check would
        // wipe the very line break or space the user just typed. Structural
        // rule instead: at most one block, and no text of any kind.
        const editableEl = noteEditableRef.current?.[0] as HTMLElement | undefined;
        const isStructurallyBlank =
          !!editableEl &&
          editableEl.children.length <= 1 &&
          (editableEl.textContent ?? '').length === 0;

        const isEffectivelyEmpty =
          !hasBogusChar &&
          !hasTabNbsp &&
          !hasNonTextContent &&
          !hasStyledBlocks &&
          editorRef.current &&
          noteEditableRef.current &&
          (editorRef.current.summernote('isEmpty') || isStructurallyBlank);

        if (isEffectivelyEmpty)
        {
          const currentHtml = noteEditableRef.current.html() as string;

          if (currentHtml !== '<p><br></p>')
          {
            editorRef.current.summernote('code', '<p><br></p>');
          }

          // Only reset cursor and format state when content actually changed to
          // empty. If HTML was already the same (no DOM mutation), a command ran
          // on the empty editor (e.g. foreColor) — skip removeFormat so the
          // pending color state the user just chose is preserved.
          const htmlActuallyChanged = (prevContent ?? '<p><br></p>') !== currentHtml;
          if (htmlActuallyChanged)
          {
            const editableEl = noteEditableRef.current[0];
            if (editableEl)
            {
              const pEl = editableEl.querySelector('p');
              if (pEl)
              {
                const range = document.createRange();
                const firstChild = pEl.firstChild;
                if (firstChild)
                {
                  range.setStartBefore(firstChild);
                }
                else
                {
                  range.setStart(pEl, 0);
                }
                range.collapse(true);
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(range);
                editableEl.focus();
                document.execCommand('removeFormat', false);

                // Reset toolbar color indicator (foreColor = color CSS, backColor = background-color CSS)
                editorRef.current.parent().find('.note-color .note-recent-color').css({
                  color: '',
                  'background-color': '',
                });
              }
            }
          }

          if (currentHtml !== '<p><br></p>') return;
        }

        onChangeRef.current?.(content);
      },
      onImageUpload: (images: File[]) =>
      {
        onImageUploadRef.current?.(images, insertImage);
      },
    });

    // Public API methods
    const focus = useCallback(() =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('focus');
      }
    }, []);

    const isEmpty = useCallback((): boolean =>
    {
      if (editorRef.current)
      {
        return editorRef.current.summernote('isEmpty');
      }
      return true;
    }, []);

    const reset = useCallback(() =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('reset');
      }
    }, []);

    const replace = useCallback(
      (content: string) =>
      {
        const noteEditable = noteEditableRef.current;
        const notePlaceholder = notePlaceholderRef.current;

        if (!noteEditable) return;

        const prevContent = noteEditable.html();
        const contentLength = content.length;

        if (prevContent !== content)
        {
          if (isEmpty() && contentLength > 0)
          {
            notePlaceholder?.hide();
          }
          else if (contentLength === 0)
          {
            notePlaceholder?.show();
          }

          noteEditable.html(content);
        }
      },
      [isEmpty],
    );

    const disable = useCallback(() =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('disable');
      }
    }, []);

    const enable = useCallback(() =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('enable');
      }
    }, []);

    const toggleState = useCallback(
      (isDisabled: boolean) =>
      {
        if (isDisabled)
        {
          disable();
        }
        else
        {
          enable();
        }
      },
      [disable, enable],
    );

    const insertImage = useCallback((url: string, filenameOrCallback?: string | Function) =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('insertImage', url, filenameOrCallback);
      }
    }, []);

    const insertNode = useCallback((node: Node) =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('insertNode', node);
      }
    }, []);

    const insertText = useCallback((text: string) =>
    {
      if (editorRef.current)
      {
        editorRef.current.summernote('insertText', text);
      }
    }, []);

    // Expose methods and refs via ref
    useImperativeHandle(
      ref,
      () => ({
        summernote: (command: string, ...args: any[]) =>
        {
          if (editorRef.current)
          {
            return editorRef.current.summernote(command, ...args);
          }
        },
        focus,
        isEmpty,
        reset,
        replace,
        disable,
        enable,
        insertImage,
        insertNode,
        insertText,
        get editor()
        {
          return editorRef.current;
        },
        get noteEditable()
        {
          return noteEditableRef.current;
        },
      }),
      [focus, isEmpty, reset, replace, disable, enable, insertImage, insertNode, insertText],
    );

    // Initialize Summernote on mount
    useEffect(() =>
    {
      const mergedOptions = {
        // summernote-lite's handleKeyMap only calls event.preventDefault() for TAB
        // when tabDisable is true (the naming is inverted from what it implies) -
        // otherwise TAB falls through to the browser's native focus-navigation
        // behavior and the cursor leaves the editor instead of inserting a tab.
        tabDisable: true,
        ...options,
        callbacks: callbacks.current,
      };

      const $ = resolveJQuery();
      editorRef.current = $(`#${uidRef.current}`);
      editorRef.current.summernote(mergedOptions);

      if (codeview)
      {
        editorRef.current.summernote('codeview.activate');
      }

      // Cleanup on unmount
      return () =>
      {
        dropdownCleanupRef.current();
        if (editorRef.current && editorRef.current.summernote)
        {
          editorRef.current.summernote('destroy');
        }
      };
    }, []); // Only run on mount/unmount

    // Re-anchor open toolbar dropdowns with position: fixed so a transformed or
    // scrollable ancestor cannot misplace or clip them.
    useEffect(() =>
    {
      const $ = resolveJQuery();
      const toolbarEl = editorRef.current?.parent()?.find('.note-toolbar')?.[0] as HTMLElement | undefined;
      if (!toolbarEl) return;

      const GAP = 8;

      const resetMenu = (menu: HTMLElement) =>
      {
        menu.style.position = '';
        menu.style.left = '';
        menu.style.top = '';
        menu.style.marginLeft = '';
      };

      const positionMenus = () =>
      {
        const groups = Array.from(
          toolbarEl.querySelectorAll<HTMLElement>('.note-btn-group.open'),
        ).filter(group => !group.parentElement?.closest('.note-dropdown-menu'));

        // Closed menus fall back to the stylesheet
        toolbarEl.querySelectorAll<HTMLElement>('.note-dropdown-menu').forEach(menu =>
        {
          if (!groups.some(group => group.contains(menu)))
          {
            resetMenu(menu);
          }
        });

        groups.forEach(group =>
        {
          const menu = group.querySelector<HTMLElement>(':scope > .note-dropdown-menu');
          if (!menu) return;

          // Read the stylesheet offset before overriding it
          resetMenu(menu);
          const cssLeft = parseFloat(window.getComputedStyle(menu).left) || 0;

          const groupRect = group.getBoundingClientRect();
          const width = menu.offsetWidth;
          const height = menu.offsetHeight;

          let left = groupRect.left + cssLeft;
          left = Math.max(GAP, Math.min(left, window.innerWidth - width - GAP));

          let top = groupRect.bottom;
          if (top + height > window.innerHeight - GAP)
          {
            top = Math.max(GAP, groupRect.top - height);
          }

          menu.style.position = 'fixed';
          menu.style.marginLeft = '0';
          menu.style.left = `${left}px`;
          menu.style.top = `${top}px`;

          // A transformed ancestor is the containing block even for fixed: correct the delta
          const placed = menu.getBoundingClientRect();
          if (Math.abs(placed.left - left) > 0.5 || Math.abs(placed.top - top) > 0.5)
          {
            menu.style.left = `${left + (left - placed.left)}px`;
            menu.style.top = `${top + (top - placed.top)}px`;
          }
        });
      };

      // Capture phase: Summernote's toggle handler calls stopImmediatePropagation
      const onToolbarClick = () => window.requestAnimationFrame(positionMenus);

      toolbarEl.addEventListener('click', onToolbarClick, true);
      window.addEventListener('resize', positionMenus);
      window.addEventListener('scroll', positionMenus, true);

      return () =>
      {
        toolbarEl.removeEventListener('click', onToolbarClick, true);
        window.removeEventListener('resize', positionMenus);
        window.removeEventListener('scroll', positionMenus, true);
        $(toolbarEl).find('.note-dropdown-menu').each((_: number, menu: HTMLElement) => resetMenu(menu));
      };
    }, []);

    // Handle value changes
    useEffect(() =>
    {
      if (typeof value === 'string' && previousValueRef.current !== value)
      {
        replace(value);
        previousValueRef.current = value;
      }
    }, [value, replace]);

    // Handle disabled state changes
    useEffect(() =>
    {
      if (typeof disabled === 'boolean' && previousDisabledRef.current !== disabled)
      {
        toggleState(disabled);
        previousDisabledRef.current = disabled;
      }
    }, [disabled, toggleState]);

    // Handle codeview changes
    useEffect(() =>
    {
      if (codeview !== previousCodeviewRef.current && editorRef.current)
      {
        const codeviewCommand = codeview ? 'codeview.activate' : 'codeview.deactivate';
        editorRef.current.summernote(codeviewCommand);
        previousCodeviewRef.current = codeview;
      }
    }, [codeview]);

    return (
      <div className={className}>
        <div id={uidRef.current} dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }} />
      </div>
    );
  },
);

ReactSummernoteCustom.displayName = 'ReactSummernoteCustom';

export default ReactSummernoteCustom;
